import {
  ChangeDetectionStrategy,
  Component,
  inject,
  DestroyRef,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { finalize, Observable } from 'rxjs';

import { Notifier } from '@core/notifications';
import { Countdown, countdown, indicateLoading } from '@core/helpers';

import { AuthState } from '../users';

import { Captcha } from './captcha.interface';
import { CaptchaService } from './captcha.service';

const CAPTCHA_EXPIRED_MESSAGE = $localize`:@@captcha.expired:Капча истекла`;

type DialogData = {
  captcha: Captcha;
  onSuccess: () => void;
};

@Component({
    selector: 'dialog-captcha',
    templateUrl: './captcha.dialog.html',
    styleUrl: './captcha.dialog.scss',
    imports: [AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CaptchaDialog {
  public readonly data = inject<DialogData>(DIALOG_DATA);
  public readonly dialogRef = inject(DialogRef);

  private readonly notifier = inject(Notifier);
  private readonly captchaService = inject(CaptchaService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authState = inject(AuthState);
  
  public countdown$!: Observable<Countdown>;

  public readonly isAnswering = signal<string|null>(null);

  private hasAnswer = false;

  constructor() {
    const timeDiff = this.authState.timeDiff();
    const expiresAt = Number(new Date(this.data.captcha.expiresAt));
    const secondsTotal = (expiresAt - Number(new Date(this.data.captcha.createdAt))) / 1000;

    this.countdown$ = countdown(this.data.captcha.expiresAt, {
      timeShift: timeDiff,
      tickInterval: 100,
      secondsTotal
    }).pipe(
      finalize(() => {
        if(!this.hasAnswer) {
          this.notifier.error(CAPTCHA_EXPIRED_MESSAGE);
          this.dialogRef.close();
        }
      })
    );
  }

  selectVariant(variant: string): void {
    this.hasAnswer = true;

    this.captchaService.submitAnswer(
      this.data.captcha.id,
      variant,
      () => this.dialogRef.close()
    )
      .pipe(
        indicateLoading(this.isAnswering, variant),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.data.onSuccess();
        this.dialogRef.close();
      });
  }
}